// The brief/écriture roster — the active brief + a switch-list of the case's
// other briefs (plan §2, option a). Pure BE-consumption: reads `useCaseStore`
// (`briefs` / `activeContext` / `selectBrief`) with narrow selectors. No new
// store, no new BE wire, no Office.js (W1/W2/W5).
//
//   §9 — logical RTL utilities; BE brief titles get `bidiProps` (titles can be
//        Arabic in an FR chrome). Meta (type/version/lang) is interpolated,
//        never concatenated.
//
// Data source nuance: the store's `briefs` array is the briefs of the
// currently-selected case (populated by `selectCase`, set by F-CASE's picker).
// The active brief is resolved from that list by `activeContext.briefId`.
//
// State machine (mirrors CaseStrip's honest-render posture):
//   1. activeContext == null            → empty state ("pick a dossier in the header")
//   2. bound but briefs not in list yet → fallback row (briefId), no other-briefs
//      (e.g. a doc hydrated from its XmlPart anchor that was never re-picked —
//       the list self-heals once the case is re-selected; honest render meanwhile)
//   3. bound + resolved                 → active brief card + other-briefs switch list

import { useTranslation } from 'react-i18next'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { bidiProps } from '@/lib/bidi'
import type { BriefSummary } from '@/domain/case/entity'

export function BriefRoster(): JSX.Element {
  const { t } = useTranslation()

  // Narrow selectors (FE selector discipline) — never the whole store object.
  const briefs = useCaseStore((s) => s.briefs)
  const activeContext = useCaseStore((s) => s.activeContext)
  const selectBrief = useCaseStore((s) => s.selectBrief)

  // ── State 1 — no case/brief bound ─────────────────────────────────────────
  if (activeContext == null) {
    return (
      <div
        data-testid="brief-roster-empty"
        className="border-b border-ink/10 px-3.5 py-4"
      >
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink/40">
          {t('plan.roster.header')}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-ink/60">
          {t('plan.roster.emptyHint')}
        </p>
      </div>
    )
  }

  const activeBrief =
    briefs.find((b) => b.id === activeContext.briefId) ?? null
  const otherBriefs = briefs.filter((b) => b.id !== activeContext.briefId)

  return (
    <div
      data-testid="brief-roster"
      className="border-b border-ink/10 bg-ink-soft/[0.03] px-3.5 py-3"
    >
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink/40">
        {t('plan.roster.header')}
      </div>

      {/* The active brief card */}
      {activeBrief != null ? (
        <div
          data-testid="brief-roster-active"
          className="mb-1.5 flex items-center gap-2.5 rounded-md border border-ink/10 bg-white px-2.5 py-2"
        >
          <span
            aria-hidden="true"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-ink font-serif text-sm font-bold italic text-gold"
          >
            {initialOf(activeBrief.title)}
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span
              className="truncate text-xs font-semibold text-ink"
              {...bidiProps(activeBrief.title)}
            >
              {activeBrief.title}
            </span>
            <span className="truncate text-[10px] text-ink/60">
              {t('plan.roster.briefMeta', {
                version: activeBrief.version,
                lang: activeBrief.language,
              })}
            </span>
          </div>
        </div>
      ) : (
        // State 2 — bound but the brief isn't in the loaded list (honest render).
        <div
          data-testid="brief-roster-fallback"
          className="mb-1.5 flex min-w-0 flex-col rounded-md border border-ink/10 bg-white px-2.5 py-2"
        >
          <span className="truncate text-xs font-semibold text-ink/80">
            {t('plan.roster.fallbackTitle')}
          </span>
          <span className="truncate text-[10px] text-ink/50">
            {t('plan.roster.fallbackMeta', {
              briefId: activeContext.briefId.slice(0, 8),
              version: activeContext.snapshotVersion,
            })}
          </span>
        </div>
      )}

      {/* Other briefs of the same case — a switch list */}
      {otherBriefs.length > 0 && (
        <div
          data-testid="brief-roster-others"
          className="flex flex-col gap-0.5"
        >
          {otherBriefs.map((b) => (
            <BriefSwitchRow
              key={b.id}
              brief={b}
              onSelect={() => selectBrief(b.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface BriefSwitchRowProps {
  brief: BriefSummary
  onSelect: () => void
}

function BriefSwitchRow({ brief, onSelect }: BriefSwitchRowProps): JSX.Element {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      data-testid={`brief-roster-switch-${brief.id}`}
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-start hover:bg-ink/5"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-ink/5 font-serif text-xs font-bold italic text-ink/60"
      >
        {initialOf(brief.title)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className="truncate text-[11px] font-medium text-ink/80"
          {...bidiProps(brief.title)}
        >
          {brief.title}
        </span>
        <span className="truncate text-[9px] text-ink/40">
          {t('plan.roster.briefMeta', {
            version: brief.version,
            lang: brief.language,
          })}
        </span>
      </div>
    </button>
  )
}

/** First grapheme of a BE title for the monogram tile. Presentation only —
 *  falls back to a neutral glyph for an empty/whitespace title. */
function initialOf(title: string): string {
  const trimmed = title.trim()
  return trimmed.length > 0 ? Array.from(trimmed)[0].toUpperCase() : '§'
}
